import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { ATTACHMENT_POLICY } from '@pulse/shared';
import { Box, Button, FormHelperText, IconButton, List, ListItem, ListItemText, Tooltip, Typography } from '@pulse/ui';
import type { ChangeEvent } from 'react';
import { useCallback, useState } from 'react';

import { formatSize, validateAttachmentSize } from '../lib/attachment-max-size.util';

import { RemoveConfirmDialog } from './RemoveConfirmDialog';

export interface StagedAttachment {
  localId: string;
  file: File;
}

// Local staging for attachments picked before the owning record exists (e.g. a Create dialog,
// before Save) — mirrors the pattern already proven in ProjectRequestsPage.tsx (staged files,
// uploaded sequentially only after the record is created). The caller snapshots `staged` at
// Save-click time and passes it to `uploadAll` explicitly, rather than this hook reading its own
// state inside an async callback, to avoid reading a stale value after further renders. Every
// returned function is wrapped in `useCallback` so callers can safely list them (e.g. `reset`)
// in a `useEffect` dependency array without the effect re-firing on every unrelated render.
export function useStagedAttachments() {
  const [staged, setStaged] = useState<StagedAttachment[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);

  const add = useCallback((file: File) => {
    setStaged((prev) => [...prev, { localId: `${Date.now()}-${Math.random()}`, file }]);
  }, []);

  const remove = useCallback((localId: string) => {
    setStaged((prev) => prev.filter((s) => s.localId !== localId));
  }, []);

  const reset = useCallback(() => {
    setStaged([]);
  }, []);

  const uploadAll = useCallback(async (items: StagedAttachment[], uploadFn: (file: File) => Promise<unknown>): Promise<boolean> => {
    setUploadingCount(items.length);
    let failed = 0;
    for (const item of items) {
      try {
        await uploadFn(item.file);
      } catch {
        failed++;
      } finally {
        setUploadingCount((n) => n - 1);
      }
    }
    return failed === 0;
  }, []);

  return { staged, add, remove, reset, uploadAll, isUploading: uploadingCount > 0 };
}

interface StagedAttachmentsPanelProps {
  staged: StagedAttachment[];
  onAdd: (file: File) => void;
  onRemove: (localId: string) => void;
  isUploading?: boolean;
}

// Visually mirrors AttachmentsPanel (same icons, List layout, ATTACHMENT_POLICY.maxFiles cap) and
// the same confirm-before-remove behavior, even though nothing is persisted yet — attachment
// delete should always confirm, in Create and Edit alike, for consistency across the app.
export function StagedAttachmentsPanel({ staged, onAdd, onRemove, isUploading }: StagedAttachmentsPanelProps) {
  const [pendingRemove, setPendingRemove] = useState<StagedAttachment | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const atLimit = staged.length >= ATTACHMENT_POLICY.maxFiles;

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const error = validateAttachmentSize(file);
    setSizeError(error);
    if (!error) onAdd(file);
  }

  return (
    <Box>
      {staged.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No attachments.</Typography>
      ) : (
        <List dense disablePadding>
          {staged.map((s) => (
            <ListItem
              key={s.localId}
              disableGutters
              secondaryAction={
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    onClick={() => setPendingRemove(s)}
                    disabled={isUploading}
                    aria-label={`Remove ${s.file.name}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText primary={s.file.name} secondary={formatSize(s.file.size)} />
            </ListItem>
          ))}
        </List>
      )}

      <Box sx={{ mt: 1 }}>
        <Button
          size="small"
          component="label"
          color={sizeError ? 'error' : undefined}
          startIcon={<AttachFileIcon />}
          disabled={atLimit || isUploading}
        >
          {isUploading ? 'Uploading…' : atLimit ? `Maximum ${ATTACHMENT_POLICY.maxFiles} files reached` : 'Attach a file'}
          <input type="file" hidden onChange={handleFileChange} />
        </Button>
        {sizeError && (
          <FormHelperText error sx={{ mx: '14px' }}>
            {sizeError}
          </FormHelperText>
        )}
      </Box>

      <RemoveConfirmDialog
        open={pendingRemove !== null}
        title={`Remove "${pendingRemove?.file.name ?? ''}"?`}
        description="This will permanently remove the attachment."
        isPending={false}
        onCancel={() => setPendingRemove(null)}
        onConfirm={() => {
          const target = pendingRemove;
          setPendingRemove(null);
          if (target) onRemove(target.localId);
        }}
      />
    </Box>
  );
}
